<template>
    <div class="flex flex-col gap-2">
        <div class="max-w-full">
            <FormContainer class="w-[300px] max-w-full">
                <Label :label="$t('profileSection.emailNotifications')"/>
                <div class="flex items-center gap-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input 
                            type="checkbox" 
                            class="sr-only peer" 
                            v-model="notifications.emailEnabled"
                            @change="saveNotifications"
                            :disabled="saving"
                        />
                        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                        <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                            {{ notifications.emailEnabled ? $t('profileSection.enabled') : $t('profileSection.disabled') }}
                        </span>
                    </label>
                </div>
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {{ notifications.emailEnabled ? $t('profileSection.emailNotificationsDesc') : $t('profileSection.emailNotificationsDisabledDesc') }}
                </p>
            </FormContainer>
            <Loading v-if="saving"/>
        </div>
        <div class="max-w-full mt-6">
            <FormContainer class="w-[300px] max-w-full">
                <Label :label="$t('profileSection.pushNotifications') || 'Push Notifications'"/>
                <div class="flex items-center gap-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input 
                            type="checkbox" 
                            class="sr-only peer" 
                            v-model="pushEnabled"
                            @change="togglePush"
                            :disabled="savingPush || !pushSupported"
                        />
                        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                        <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                            {{ pushEnabled ? $t('profileSection.enabled') : $t('profileSection.disabled') }}
                        </span>
                    </label>
                </div>
                <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {{ pushHint }}
                </p>

                <!-- Error message for Brave/Privacy browsers (web); setup hint (native) -->
                <div v-if="pushError" class="mt-4 p-3 text-xs text-amber-800 bg-amber-50 rounded-lg dark:bg-gray-800 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                    <p class="font-semibold mb-1">{{ $t('profileSection.pushErrorTitle') }}</p>
                    <p v-if="isNativeApp">{{ $t('profileSection.pushErrorNative') }}</p>
                    <p v-else v-html="$t('profileSection.pushErrorBrave')"></p>
                </div>
            </FormContainer>
            <Loading v-if="savingPush"/>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import toastStore from '@/store/toast.store'
import pushService from '@/service/push.service'

const { t } = useI18n();
const isNativeApp = pushService.isNative();
const pushHint = computed(() => {
  if (isNativeApp) return t('profileSection.pushNativeDesc');
  return pushSupported.value ? t('profileSection.pushWebDesc') : t('profileSection.pushUnsupported');
});

const notifications = ref({
    emailEnabled: true
})
const saving = ref(false)

const pushSupported = ref(pushService.isSupported())
const pushEnabled = ref(false)
const savingPush = ref(false)
const pushError = ref(false)

onMounted(async () => {
    try {
        const response = await fetch('/api/user/notifications')
        if (response.ok) {
            const data = await response.json()
            notifications.value = {
                emailEnabled: data.emailEnabled !== false, // Default to true if not set
                locale: data.locale || 'en-US'
            }
        } else if (response.status === 404) {
            // If endpoint doesn't exist yet, use default value
            notifications.value = {
                emailEnabled: true,
                locale: 'en-US'
            }
        } else {
            throw new Error('Failed to load notification preferences')
        }
    } catch (error) {
        // If endpoint doesn't exist, use default
        notifications.value = {
            emailEnabled: true,
            locale: 'en-US'
        }
        console.warn('Could not load notification preferences:', error)
    }

    if (pushSupported.value) {
        try {
            const sub = await pushService.getSubscription()
            pushEnabled.value = !!sub
        } catch(e) {
            console.error('Error checking push subscription', e)
        }
    }
})

const saveNotifications = async () => {
    saving.value = true
    try {
        const response = await fetch('/api/user/notifications', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(notifications.value)
        })
        
        if (response.ok) {
            toastStore.dispatch('addMessage', {
                message: t('profileSection.notificationsUpdated') || 'Notification preferences updated successfully',
                icon: 'success'
            })
        } else {
            throw new Error('Failed to update notification preferences')
        }
    } catch (error) {
        toastStore.dispatch('addMessage', {
            message: t('profileSection.notificationsFailed') || 'Failed to update notification preferences',
            icon: 'error'
        })
    } finally {
        saving.value = false
    }
}

const togglePush = async () => {
    savingPush.value = true
    pushError.value = false
    try {
        if (pushEnabled.value) {
            await pushService.subscribe()
            toastStore.dispatch('addMessage', {
                message: t('profileSection.pushEnabledToast'),
                icon: 'success'
            })
        } else {
            await pushService.unsubscribe()
            toastStore.dispatch('addMessage', {
                message: t('profileSection.pushDisabledToast'),
                icon: 'info'
            })
        }
    } catch (error) {
        console.error('Push error:', error)
        pushEnabled.value = !pushEnabled.value // revert
        pushError.value = true
        toastStore.dispatch('addMessage', {
            message: t('profileSection.pushFailedToast'),
            icon: 'error'
        })
    } finally {
        savingPush.value = false
    }
}
</script>

